import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';

// Attachment URLs are read back from Firestore docs other workspace members
// wrote, and end up in href/src/window.open - only a Firebase Storage
// download URL is ever a real one, anything else (javascript:, data:, a
// phishing site) is dropped instead of rendered.
export function isStorageUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname === 'firebasestorage.googleapis.com';
  } catch {
    return false;
  }
}

// For links to the outside web (search result sources): http(s) only.
export function isWebUrl(url) {
  try {
    return ['http:', 'https:'].includes(new URL(url).protocol);
  } catch {
    return false;
  }
}

export async function uploadAttachment(file, uid, conversationId) {
  const safeName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_')}`;
  const fileRef = ref(storage, `ia/${uid}/${conversationId}/${safeName}`);
  await uploadBytes(fileRef, file, { contentType: file.type });
  return getDownloadURL(fileRef);
}

export async function uploadBlob(blob, uid, conversationId, name) {
  const safeName = `${Date.now()}-${name.replace(/[^a-zA-Z0-9.\-_]/g, '_')}`;
  const fileRef = ref(storage, `ia/${uid}/${conversationId}/${safeName}`);
  await uploadBytes(fileRef, blob, { contentType: blob.type || 'image/jpeg' });
  return getDownloadURL(fileRef);
}

// Loads a video file off-screen, seeks to `atSeconds`, and resolves a JPEG
// Blob of that frame - used to give the vision model something to look at
// for a video attachment without sending/decoding the whole video.
export function captureVideoFrameFromFile(file, atSeconds = 1) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.style.cssText = 'position:absolute;left:-9999px;width:1px;height:1px;';
    document.body.appendChild(video);

    const cleanup = () => {
      URL.revokeObjectURL(video.src);
      video.remove();
    };

    video.onloadedmetadata = () => {
      video.currentTime = Math.min(atSeconds, (video.duration || 0) / 2 || 0);
    };
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      canvas.toBlob((blob) => {
        cleanup();
        blob ? resolve(blob) : reject(new Error('capture failed'));
      }, 'image/jpeg', 0.85);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('video load failed'));
    };
    video.src = URL.createObjectURL(file);
  });
}

// Captures the current frame of a live <video> element (e.g. a screen-share
// stream) as a JPEG Blob. If the video hasn't decoded its first frame yet
// (videoWidth/videoHeight still 0 - e.g. the user sent a message right after
// starting the share, before playback caught up), waits briefly for it
// rather than capturing a blank frame.
export function captureStreamFrame(videoEl) {
  return new Promise((resolve, reject) => {
    const capture = () => {
      const canvas = document.createElement('canvas');
      canvas.width = videoEl.videoWidth;
      canvas.height = videoEl.videoHeight;
      canvas.getContext('2d').drawImage(videoEl, 0, 0);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('capture failed'))), 'image/jpeg', 0.85);
    };

    if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
      capture();
      return;
    }

    const timeoutId = setTimeout(() => {
      videoEl.removeEventListener('loadeddata', onReady);
      reject(new Error('screen share video not ready'));
    }, 3000);

    const onReady = () => {
      clearTimeout(timeoutId);
      videoEl.removeEventListener('loadeddata', onReady);
      capture();
    };
    videoEl.addEventListener('loadeddata', onReady);
  });
}
